'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface QuickNavItem {
  label: string;
  href: string;
  count?: number;
}

interface QuickNavigationProps {
  primaryItems: QuickNavItem[];
  evidenceItems: QuickNavItem[];
}

export default function QuickNavigation({ primaryItems, evidenceItems }: QuickNavigationProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [navHeight, setNavHeight] = useState(0);
  const [mainNavHeight, setMainNavHeight] = useState(72); // Default navbar height
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);
  const isStickyRef = useRef(false);
  const [anchorTop, setAnchorTop] = useState<number | null>(null);

  useEffect(() => {
    if (!navRef.current) return;

    const updateDimensions = () => {
      if (!navRef.current) return;
      // Only measure the anchor while the bar is in normal flow — once fixed,
      // offsetTop reflects the viewport, not the docking point.
      if (!isStickyRef.current) {
        setAnchorTop(navRef.current.offsetTop);
      }
      setNavHeight(navRef.current.offsetHeight);

      // The marketing header is `fixed` and already offset by the promo banner
      // via `body.promo-banner-visible .marketing-navbar { top: 3.5rem }`, so
      // its viewport-relative bottom is exactly where this bar should dock.
      const header = document.querySelector<HTMLElement>('.marketing-navbar, header.fixed');
      setMainNavHeight(header ? Math.round(header.getBoundingClientRect().bottom) : 72);
    };

    // Wait for DOM to be ready
    const timer = setTimeout(updateDimensions, 100);
    window.addEventListener('resize', updateDimensions);
    // Re-measure when the promo banner is dismissed (body class flips and the
    // fixed header slides up 56px).
    const observer = new MutationObserver(updateDimensions);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateDimensions);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (anchorTop === null) return;

    const handleScroll = () => {
      const sticky = window.scrollY + mainNavHeight >= anchorTop;
      isStickyRef.current = sticky;
      setIsSticky(sticky);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [anchorTop, mainNavHeight]);

  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    href: string,
  ) => {
    event.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      const top = (target as HTMLElement).offsetTop - mainNavHeight - navHeight - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    // Close mobile menu after clicking
    setIsMobileMenuOpen(false);
  };

  const renderItem = (item: QuickNavItem, index: number, totalItems: number, isMobile = false) => {
    const isLastTwoItems = index >= totalItems - 2;

    if (isLastTwoItems) {
      return (
        <button
          key={item.href}
          onClick={event => handleClick(event, item.href)}
          className={`group inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-medium text-primary transition-colors hover:border-primary hover:bg-primary hover:text-white ${
            isMobile ? 'w-full justify-center' : ''
          }`}
        >
          {typeof item.count === 'number' && (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary transition-colors group-hover:bg-white/20 group-hover:text-white">
              {item.count}
            </span>
          )}
          <span className="transition-colors group-hover:text-inherit">{item.label}</span>
        </button>
      );
    }

    return (
      <a
        key={item.href}
        href={item.href}
        onClick={event => handleClick(event, item.href)}
        className={`group inline-flex items-center gap-3 border-b-2 border-transparent px-4 py-3 text-sm font-medium text-default-600 transition-colors hover:border-primary hover:text-primary ${
          isMobile ? 'w-full justify-center' : ''
        }`}
      >
        {typeof item.count === 'number' && (
          <span className="text-xs font-semibold uppercase tracking-wide text-primary/70 group-hover:text-primary">
            {item.count}
          </span>
        )}
        <span className="transition-colors group-hover:text-primary">{item.label}</span>
      </a>
    );
  };

  if (primaryItems.length === 0 && evidenceItems.length === 0) {
    return null;
  }

  const allItems = [...primaryItems, ...evidenceItems];
  const totalItems = allItems.length;

  return (
    <>
      {isSticky && <div style={{ height: navHeight }} />}
      <div
        ref={navRef}
        className={`transition-all ${
          isSticky
            ? `fixed left-0 right-0 z-[60] border-b border-default-200 bg-default-100/95 backdrop-blur-md shadow-md`
            : ''
        }`}
        style={isSticky ? { top: `${mainNavHeight}px` } : {}}
      >
        {/* Desktop Navigation */}
        <div className="hidden md:flex container mx-auto px-4 sm:px-6 lg:px-8 items-center justify-start gap-6 overflow-x-auto">
          {allItems.map((item, index) => renderItem(item, index, totalItems, false))}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <div className="mx-auto w-full px-4">
            <button
              type="button"
              className="w-full flex items-center justify-between bg-transparent text-default-700 hover:bg-default-100 py-3 px-4 rounded-xl"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="text-sm font-medium">Quick Navigation</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isMobileMenuOpen && (
              <div className="mt-2 space-y-2 pb-4">
                {allItems.map((item, index) => renderItem(item, index, totalItems, true))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
